/**
 * Kwolly — Collection List JS
 * ---------------------------------------------------------------
 * Responsibilities:
 *  1. Stagger-reveal: IntersectionObserver watches [data-kwolly-reveal]
 *     items and applies .is-visible with a per-item CSS transition-delay.
 *     Respects prefers-reduced-motion (skips delay, still adds class for
 *     browsers that don't support the CSS-level motion query).
 *  2. Theme Editor: re-initialises on shopify:section:load so the reveal
 *     works correctly when a merchant edits the section live.
 *
 * Vanilla JS only — no external dependencies.
 * ---------------------------------------------------------------
 */

(function () {
  'use strict';

  var REVEAL_ATTR   = 'data-kwolly-reveal';
  var VISIBLE_CLASS = 'is-visible';
  var STAGGER_MS    = 90;   /* ms between each item's reveal */
  var THRESHOLD     = 0.1;  /* 10 % of item visible triggers reveal */
  var ROOT_MARGIN   = '0px 0px -4% 0px';

  var reducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Attach an IntersectionObserver to all [data-kwolly-reveal] items
   * inside a given root element.
   *
   * Progressive enhancement: adds .kwolly-cl--js-ready to the section
   * so CSS can hide items safely — they remain visible without JS.
   *
   * @param {Element} root
   */
  function initReveal(root) {
    var items = Array.prototype.slice.call(
      root.querySelectorAll('[' + REVEAL_ATTR + ']')
    );

    if (items.length === 0) return;

    /* Enable the hidden-by-default CSS rule now that JS is confirmed live */
    root.classList.add('kwolly-cl--js-ready');

    /* If reduced-motion: skip animation entirely — mark all visible */
    if (reducedMotion) {
      items.forEach(function (item) {
        item.classList.add(VISIBLE_CLASS);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add(VISIBLE_CLASS);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: THRESHOLD, rootMargin: ROOT_MARGIN }
    );

    /* Apply staggered transition-delay before observing */
    items.forEach(function (item, index) {
      item.style.transitionDelay = index * STAGGER_MS + 'ms';
      observer.observe(item);
    });
  }

  /**
   * Reset and re-initialise reveal for a section — used by Theme Editor.
   *
   * @param {Element} section
   */
  function reinitSection(section) {
    var items = Array.prototype.slice.call(
      section.querySelectorAll('[' + REVEAL_ATTR + ']')
    );

    section.classList.remove('kwolly-cl--js-ready');

    items.forEach(function (item) {
      item.style.transitionDelay = '';
      item.classList.remove(VISIBLE_CLASS);
    });

    /* Small delay so the DOM settles after the editor reload */
    window.requestAnimationFrame(function () {
      initReveal(section);
    });
  }

  /* ── Initial page load ───────────────────────────────────────── */
  document.querySelectorAll('.kwolly-cl').forEach(initReveal);

  /* ── Theme Editor events ─────────────────────────────────────── */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.kwolly-cl');
    if (section) reinitSection(section);
  });

  document.addEventListener('shopify:block:select', function (event) {
    /* When merchant selects a specific block, ensure it's visible */
    var item = event.target.closest('[' + REVEAL_ATTR + ']');
    if (item) {
      item.style.transitionDelay = '0ms';
      item.classList.add(VISIBLE_CLASS);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    /* Re-run full reveal so all items are visible after block deselect */
    var section = event.target.closest('.kwolly-cl');
    if (section) reinitSection(section);
  });

})();
